import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core'

import type { FeatureKey } from '@app/features/feature-keys.generated'
import { FeatureService } from '@app/services/feature.service'

/**
 * Structural directive that renders its template only when the named feature
 * flag is enabled for the active tenant.
 *
 *   <div *hasFeature="'sidebar.overview'">…</div>
 *
 * Flag values come from `config/features/{TENANT}.json`, imported at build
 * time via `src/environments/tenant.ts` — there is no async fetch.
 */
@Directive({
  standalone: true,
  selector: '[hasFeature]',
})
export class HasFeatureDirective {
  private rendered = false

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private features: FeatureService,
  ) {}

  @Input() set hasFeature(featureKey: FeatureKey | '' | undefined) {
    // No feature key ⇒ always render, to match the route guard (an entry
    // without a `feature` is ungated). This avoids the footgun where a
    // menu/route item that forgot its key is silently hidden yet still routable.
    const enabled = !featureKey || this.features.has(featureKey)
    if (enabled && !this.rendered) {
      this.viewContainer.createEmbeddedView(this.templateRef)
      this.rendered = true
    } else if (!enabled && this.rendered) {
      this.viewContainer.clear()
      this.rendered = false
    }
  }
}
